import {lazy, Suspense} from 'react'

import {DefignProvider} from '@defign/ui'
import {Route, Routes} from 'react-router-dom'
import {HistoryRouter as Router} from 'redux-first-history/rr6'

import ErrorBoundary from '@web/components/errorBoundary'
// import {isRealProfile} from '@web/core/config'
import {isAlphaProfile, isDevProfile, isLocalProfile, isTestProfile} from '@web/core/config'
import {OrderEntry} from '@web/core/constants'
import {
    ROUTER_ORDER_COMMON,
    ROUTER_ORDER_COUPON,
    ROUTER_ORDER_DELIVERY,
    ROUTER_ORDER_DELIVERY_TRACKING,
    ROUTER_ORDER_DETAIL,
    ROUTER_ORDER_FAMILY_PAY,
    ROUTER_ORDER_PAYMENT_METHODS,
    ROUTER_ORDER_PURCHASE_CONFIRMATION,
    ROUTER_ORDER_RESULT,
    ROUTER_ORDER_SALE_HISTORY,
    ROUTER_ORDER_SALE_SETTLEMENT,
    ROUTER_ORDER_SHEET,
    ROUTER_ORDER_SHEET_BOOKING,
    ROUTER_ORDER_SHEET_SUBSCRIPTION,
    ROUTER_ORDER_BOOK_PARCEL_PICKUP,
    ROUTER_ORDER_MERCHANT,
    ROUTER_DELIVERY_INSTRUCTION,
    // ROUTER_ORDER_CLAIM,
} from '@web/core/routes'
import {TIMELINE_MOBILE_URL, TIMELINE_URL} from '@web/core/urls'
import {goBackOrFallbackPage} from '@web/helpers/browser'
import {useCommonLogInit} from '@web/helpers/hooks'
import {applyTraceTab, Sentry, withWebConsole} from '@web/helpers/log'
import {GlobalProvider} from '@web/helpers/modal'
import {isMobile} from '@web/helpers/userAgent'
import {useCommonInit, useErrorLogger} from '@web/hooks'
import {getPageSetByPathname} from '@web/slices/common/slice'
import {createReduxHistory} from '@web/slices/router'
import {GeneralErrorTemplate} from '@web/ui/templates'

import '@web/scss/npay_global.scss'
import '@defign/ui/styles/defign.css'
import '@defign/icon/styles/index.css'

import useStore from './lib/useStore'

const OrderRoutes = lazy(() => import('@web/pages/order'))
const OrderSheetCheckOutRoutes = lazy(() =>
    import('@web/pages/order').then((module) => ({default: module.OrderSheetCheckOutRoutes})),
)
const PurchaseConfirmationRoutes = lazy(() => import('@web/pages/purchaseConfirmation'))
const FamilyPayRoutes = lazy(() => import('@web/pages/familyPay'))
const FamilyPayUserRoutes = lazy(() =>
    import('@web/pages/familyPay').then((module) => ({default: module.FamilyPayUserRoutes})),
)
const SmartStoreRoutes = lazy(() => import('@web/pages/smartstore'))
const OrderDetailRoutes = lazy(() => import('@web/pages/orderDetail'))
const OrderResultRoutes = lazy(() => import('@web/pages/orderResult'))
const CouponRoutes = lazy(() => import('@web/pages/management/coupon'))
const PaymentMethodRoutes = lazy(() => import('@web/pages/management/paymentMethod'))
const CommonRoutes = lazy(() => import('@web/pages/common'))
const SaleHistoryRoutes = lazy(() => import('@web/pages/saleHistory'))
const SaleSettlementRoutes = lazy(() => import('@web/pages/saleSettlement'))
const BookingRoutes = lazy(() => import('@web/pages/booking'))
const SubscriptionRoutes = lazy(() => import('@web/pages/shoppingSubscription'))
const DeliveryRoutes = lazy(() => import('@web/pages/delivery'))
const DeliveryTrackingRoutes = lazy(() => import('@web/pages/deliveryTracking'))
const FleaMarketRoutes = lazy(() => import('@web/pages/fleaMarket'))
const BookParcelPickupRoutes = lazy(() => import('@web/pages/bookParcelPickup'))
const MerchantInfoRoutes = lazy(() => import('@web/pages/merchantInfo'))
const DeliveryInstructionRoutes = lazy(() => import('@web/pages/deliveryInstruction'))
// const RequestClaimRoutes = lazy(() => import('@web/pages/claim/request'))
// const CollectClaimRoutes = lazy(() => import('@web/pages/claim/collect'))
// const PaymentClaimRoutes = lazy(() => import('@web/pages/claim/payment'))
// const ResultClaimRoutes = lazy(() => import('@web/pages/claim/result'))
// const DetailClaimRoutes = lazy(() => import('@web/pages/claim/detail'))
const NotFound = lazy(() => import('@web/features/notFound'))

const RedirectComponent = ({
    originPath = '/ordersheet',
    redirectPath = '/order/checkout',
}: {
    originPath?: string
    redirectPath?: string
}) => {
    const url = `${window.location.href.replace(originPath, redirectPath)}`
    window.location.replace(url)
    return null
}

export function App() {
    useCommonInit()
    useCommonLogInit()
    useErrorLogger()

    const store = useStore()

    return (
        <ErrorBoundary
            pageSet={getPageSetByPathname()}
            fallback={
                <GeneralErrorTemplate
                    mainText="잠시 후 다시 시도해주세요"
                    subText={
                        <>
                            서비스 이용에 불편을 드려 죄송합니다.
                            <br />
                            빠른 해결을 위해 최선을 다하겠습니다.
                        </>
                    }
                    buttonText="이전 페이지"
                    onClickButton={() => goBackOrFallbackPage(isMobile ? TIMELINE_MOBILE_URL : TIMELINE_URL)}
                />
            }
        >
            <DefignProvider>
                <GlobalProvider>
                    <Router history={createReduxHistory(store)}>
                        <Suspense fallback={null}>
                            <Routes>
                                <Route
                                    path={`/ordersheet/bridge/:orderChannel/:registerOrderKey?/:merchantNo?`}
                                    element={<RedirectComponent />}
                                />
                                <Route
                                    path={`/ordersheet/familyPay/agree/*`}
                                    element={<RedirectComponent redirectPath={'/order'} />}
                                />
                                <Route
                                    path={`/ordersheet/familyPay/withdraw`}
                                    element={<RedirectComponent redirectPath={'/order'} />}
                                />
                                {/* 주문서 경로 /ordersheet/{channel} */}
                                <Route path={`/ordersheet/familyPay/*`} element={<FamilyPayRoutes />} />
                                <Route
                                    path={ROUTER_ORDER_FAMILY_PAY.MAIN.getPath('*')}
                                    element={<FamilyPayUserRoutes />}
                                />
                                <Route
                                    path={ROUTER_ORDER_PURCHASE_CONFIRMATION.MAIN.getPath('*')}
                                    element={<PurchaseConfirmationRoutes />}
                                />
                                <Route path={ROUTER_ORDER_DETAIL.MAIN.getPath('*')} element={<OrderDetailRoutes />} />
                                {/* 주문완료 경로 /order/result/{channel} */}
                                <Route path={ROUTER_ORDER_RESULT.MAIN.getPath('*')} element={<OrderResultRoutes />} />
                                <Route path={ROUTER_ORDER_COMMON.MAIN.getPath('*')} element={<CommonRoutes />} />
                                {/* 판매 경로 /order/sale/{*} */}
                                <Route
                                    path={ROUTER_ORDER_SALE_HISTORY.MAIN.getPath('*')}
                                    element={<SaleHistoryRoutes />}
                                />
                                <Route
                                    path={ROUTER_ORDER_SALE_SETTLEMENT.MAIN.getPath('*')}
                                    element={<SaleSettlementRoutes />}
                                />
                                <Route
                                    path={ROUTER_ORDER_MERCHANT.MAIN.getPath('*')}
                                    element={<MerchantInfoRoutes />}
                                />
                                <Route path={`/order/*`} element={<OrderRoutes />} />
                                <Route
                                    path={`${ROUTER_ORDER_SHEET.MALL.CHECK_OUT.getPath('*')}`}
                                    element={<OrderSheetCheckOutRoutes channel={OrderEntry.CHECKOUT} />}
                                />
                                <Route
                                    path={`${ROUTER_ORDER_SHEET.EASY_BOOKING.CHECK_OUT.getPath('*')}`}
                                    element={<OrderSheetCheckOutRoutes channel={OrderEntry.EASY_BOOKING} />}
                                />
                                <Route
                                    path={`${ROUTER_ORDER_SHEET.LOCAL_PAY.CHECK_OUT.getPath('*')}`}
                                    element={<OrderSheetCheckOutRoutes channel={OrderEntry.LOCAL_PAY} />}
                                />
                                <Route
                                    path={`${ROUTER_ORDER_SHEET.BLOG.CHECK_OUT.getPath('*')}`}
                                    element={<OrderSheetCheckOutRoutes channel={OrderEntry.NAVER_BLOG} />}
                                />
                                <Route
                                    path={ROUTER_ORDER_SHEET.FLEA_MARKET.CHECK_OUT.getPath('*')}
                                    element={<FleaMarketRoutes />}
                                />
                                <Route path={`/ordersheet/seller/*`} element={<SmartStoreRoutes />} />
                                <Route path={`${ROUTER_ORDER_COUPON.ROOT.getPath('*')}`} element={<CouponRoutes />} />
                                <Route
                                    path={`${ROUTER_ORDER_PAYMENT_METHODS.ROOT.getPath('*')}`}
                                    element={<PaymentMethodRoutes />}
                                />
                                <Route
                                    path={`${ROUTER_ORDER_SHEET_BOOKING.ROOT.getPath('*')}`}
                                    element={<BookingRoutes />}
                                />
                                <Route
                                    path={`${ROUTER_ORDER_SHEET_SUBSCRIPTION.ROOT.getPath('*')}`}
                                    element={<SubscriptionRoutes />}
                                />
                                <Route
                                    path={`${ROUTER_ORDER_DELIVERY_TRACKING.ROOT.getPath('*')}`}
                                    element={<DeliveryTrackingRoutes />}
                                />
                                <Route path={`${ROUTER_ORDER_DELIVERY.DELIVERY}/*`} element={<DeliveryRoutes />} />
                                <Route path={`${ROUTER_ORDER_DELIVERY.ZIPCODE}/*`} element={<DeliveryRoutes />} />
                                <Route
                                    path={`${ROUTER_ORDER_BOOK_PARCEL_PICKUP.ROOT.getPath('*')}`}
                                    element={<BookParcelPickupRoutes />}
                                />
                                <Route
                                    path={`${ROUTER_DELIVERY_INSTRUCTION.MAIN}/*`}
                                    element={<DeliveryInstructionRoutes />}
                                />
                                {/* {
                                    // TODO[클레임]: 리얼배포 전 제거 필요
                                    !isRealProfile && (
                                        <>
                                            <Route
                                                path={ROUTER_ORDER_CLAIM.REQUEST.ROOT.getPath('*')}
                                                element={<RequestClaimRoutes />}
                                            />
                                            <Route
                                                path={ROUTER_ORDER_CLAIM.COLLECT.ROOT.getPath('*')}
                                                element={<CollectClaimRoutes />}
                                            />
                                            <Route
                                                path={ROUTER_ORDER_CLAIM.PAYMENT.ROOT.getPath('*')}
                                                element={<PaymentClaimRoutes />}
                                            />
                                            <Route
                                                path={ROUTER_ORDER_CLAIM.RESULT.ROOT.getPath('*')}
                                                element={<ResultClaimRoutes />}
                                            />
                                            <Route
                                                path={ROUTER_ORDER_CLAIM.DETAIL.ROOT.getPath('*')}
                                                element={<DetailClaimRoutes />}
                                            />
                                        </>
                                    )
                                } */}
                                <Route path={'/*'} element={<NotFound />} />
                            </Routes>
                        </Suspense>
                    </Router>
                </GlobalProvider>
            </DefignProvider>
        </ErrorBoundary>
    )
}

export default Sentry.withProfiler(
    withWebConsole(App, {
        enabled: isLocalProfile || isAlphaProfile || isTestProfile || isDevProfile,
        tabs: [
            applyTraceTab({
                id: 'common-trace',
                name: 'Trace',
            }),
        ],
    }),
)
